import { useEffect } from 'react';

/**
 * Reveals elements carrying the `reveal` class as they scroll into view.
 * The hidden state lives behind `.js .reveal` in index.css, so if scripting or
 * IntersectionObserver is unavailable the content simply renders visible.
 */
export default function useReveal(deps = []) {
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll('.reveal:not(.is-visible)'));
    if (nodes.length === 0) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    nodes.forEach((node) => observer.observe(node));

    // Safety net: if the observer never fires (hidden tab, prerender,
    // snapshotting), reveal everything rather than leaving sections invisible.
    const fallback = window.setTimeout(() => {
      document
        .querySelectorAll('.reveal:not(.is-visible)')
        .forEach((node) => node.classList.add('is-visible'));
    }, 3000);

    return () => {
      window.clearTimeout(fallback);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Counts a number up when its element enters view. Returns a ref callback.
 * Falls back to the final value when motion is reduced.
 */
export function useCountUp(ref, target, { decimals = 0, duration = 1100 } = {}) {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof target !== 'number' || Number.isNaN(target)) return undefined;

    const reduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const settle = () => {
      el.textContent = target.toFixed(decimals);
    };

    if (reduced || typeof IntersectionObserver === 'undefined') {
      settle();
      return undefined;
    }

    let frame = null;
    let start = null;

    const step = (ts) => {
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = (target * eased).toFixed(decimals);
      if (p < 1) frame = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          frame = requestAnimationFrame(step);
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ref, target, decimals, duration]);
}
